import { FooterLinks, SocialLinks } from "./constant";
import { Link, ExternalLink } from "src/ccl/navigation";
import { Container, Flex, Grid, LegacyContainer } from "src/ccl/layout";
import {
  FLUID_GUTTER,
  useDashboardFrame,
} from "src/ccl/templates/DashboardFrame";

import { Icon, Text } from "src/ccl/document";
import { useStoreModel, useUserKindContext } from "src/hooks";
import { styled } from "src/ccl/stitches";
import { sendToAmplitude, trackEvent } from "src/utils/analytics";

const StyledExternalLink = styled(ExternalLink, {
  textDecoration: "none",
  display: "flex",
});

const linkStyles = {
  textDecoration: "none",
  color: "$black",
};

const FooterLink = styled(Link, linkStyles);

const FooterButton = styled("button", {
  ...linkStyles,
  background: "none",
  border: 0,
  cursor: "pointer",
  font: "inherit",
  padding: 0,
  "&:hover": {
    color: "$grey6",
  },
});

const FooterText = styled(Text, linkStyles);

const Meta = styled(Flex, {
  justifyContent: "space-between",
  flexDirection: "row",
});

const MetaLinks = styled(Flex, {
  flexDirection: "row",
  gap: "$2",
});

const MetaLink = styled("div", {
  mb: "$5",
  "@bp3": {
    ml: "$5",
    mb: 0,
  },
});

const StyledFooter = styled("footer", {
  borderTop: "1px solid $grey3",
  padding: "$10",
  mt: "$10",
});

const sendAmplitudeEvent = (eventName: string) => () =>
  sendToAmplitude(eventName);

/**
 * Inside a fluid dashboard the footer sits on the frame's gutters, the same
 * ones DashboardPage gives its header and content, with no inset of its own.
 * Everywhere else it keeps the centred legacy container.
 */
const FluidFrame = ({ children }: { children: React.ReactNode }) => (
  <Container
    css={{
      flexDirection: "column",
      "@lg": FLUID_GUTTER,
    }}
  >
    {children}
  </Container>
);

const LegacyFrame = ({ children }: { children: React.ReactNode }) => (
  <LegacyContainer variant="gutter">{children}</LegacyContainer>
);

export const RebrandFooter = () => {
  const currentUser = useStoreModel("currentUser");
  const userKindContext = useUserKindContext(currentUser);
  const canCreateJob =
    userKindContext === "signedOut" || userKindContext === "client";
  const { fluid } = useDashboardFrame();
  const Frame = fluid ? FluidFrame : LegacyFrame;

  return (
    <Frame>
      <StyledFooter css={fluid ? { px: 0 } : undefined}>
        <Flex
          css={{
            flexDirection: "column",
            gap: "$10",
            "@bp2": {
              justifyContent: "space-between",
              alignItems: "center",
              flexDirection: "row",
            },
          }}
        >
          <Flex
            css={{
              flexDirection: "column",
              gap: "$10",
              "@bp2": {
                flexDirection: "row",
              },
            }}
          >
            <FooterLink to="/" aria-label="Contact home">
              <Icon variant="rebrandLogo" size={30} />
            </FooterLink>
            <Grid
              css={{
                gridColumns: 2,
                gap: "$10",
                "@bp2": {
                  gridColumns: 7,
                  gap: "$5",
                  alignItems: "center",
                  textAlign: "center",
                },
              }}
            >
              {FooterLinks.filter(
                (footer) => !footer.isCreateJob || canCreateJob,
              ).map((footer, index) =>
                footer.external ? (
                  <StyledExternalLink
                    key={index}
                    to={footer.link}
                    target="_blank"
                    eventName={footer.eventName ?? `footer:${footer.text}`}
                  >
                    <FooterText>{footer.text}</FooterText>
                  </StyledExternalLink>
                ) : footer.isCookies ? (
                  <FooterButton
                    key={index}
                    type="button"
                    onClick={() => {
                      if (footer.eventName) {
                        trackEvent(footer.eventName);
                      }
                      if (window.cookiehub) {
                        window.cookiehub.openSettings();
                      }
                    }}
                  >
                    {footer.text}
                  </FooterButton>
                ) : (
                  <FooterLink
                    key={index}
                    to={footer.link}
                    onClick={
                      footer.amplitudeEvent
                        ? sendAmplitudeEvent(footer.amplitudeEvent)
                        : undefined
                    }
                  >
                    {footer.text}
                  </FooterLink>
                ),
              )}
            </Grid>
          </Flex>

          <Meta>
            <MetaLinks>
              {SocialLinks.map((social, index) => (
                <MetaLink key={index}>
                  <StyledExternalLink
                    to={social.link}
                    target="_blank"
                    aria-label={social.accessibleName}
                    eventName={`footer:${social.iconVariant}`}
                  >
                    <Icon size={20} variant={social.iconVariant} />
                  </StyledExternalLink>
                </MetaLink>
              ))}
            </MetaLinks>
          </Meta>
        </Flex>
      </StyledFooter>
    </Frame>
  );
};
